import { useContextValue } from 'app/shared/hooks/use-context-value';
import { useOpenParams, updateStoredAuthToken } from 'app/shared/hooks/use-open-params';
import { HQO_BRAND, HqoHeadersEnum } from 'app/shared/consts/enums';
import { createContext, FC, PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react';

export interface ApiContextValue {
  apiUrl: string;
  authToken: string;
  setAuthToken: (token: string) => void;
  getAuthTokenValue: () => Promise<string>;
  getAuthHeaderValue: () => Promise<string>;
  getAuthHeaders: () => Promise<Record<string, string>>;
  getDefaultHeaders: (buildingUuid?: string) => Record<string, string>;
}

const ApiContext = createContext<ApiContextValue>({
  apiUrl: process.env.REACT_APP_API_URL || '',
  authToken: '',
  setAuthToken: () => undefined,
  getAuthTokenValue: () => Promise.resolve(''),
  getAuthHeaderValue: () => Promise.resolve(''),
  getAuthHeaders: () => Promise.resolve({}),
  getDefaultHeaders: () => ({}),
});

// Host returns a raw JWT; query-param tokens carry a "bearer " prefix. Normalise both.
const normalizeAuthToken = (token: string): string => {
  if (!token) {
    return '';
  }

  return /^bearer\s/i.test(token) ? token : `Bearer ${token}`;
};

export const ApiProvider: FC<PropsWithChildren> = ({ children }) => {
  const { authToken: initialAuthToken, apiUrl, appBrand } = useOpenParams();

  // Stateful so useTokenRefresh can swap in a fresh token after the host re-auths.
  const [authToken, setAuthTokenState] = useState<string>(initialAuthToken);

  // The ref mirrors the token synchronously. The header getters read it (not the render
  // closure) so a refetch triggered immediately after setAuthToken — e.g. the
  // invalidateQueries in useTokenRefresh, which runs before React re-renders — uses the
  // FRESH token instead of the stale one (which would 401 again and strand the error page).
  const authTokenRef = useRef<string>(initialAuthToken);

  const setAuthToken = useCallback((token: string) => {
    const normalized = normalizeAuthToken(token);
    authTokenRef.current = normalized;
    setAuthTokenState(normalized);
    updateStoredAuthToken(normalized);
  }, []);

  const contextValue = useMemo(
    () => ({
      apiUrl: apiUrl ? new URL(apiUrl).origin : '',
      authToken,
      setAuthToken,
      getAuthTokenValue: async () => authTokenRef.current?.replace(/bearer /i, ''),
      getAuthHeaderValue: async () => authTokenRef.current,
      getAuthHeaders: async () => ({ Authorization: authTokenRef.current }),
      getDefaultHeaders: (buildingUuid?: string): Record<string, string> => ({
        [HqoHeadersEnum.BRAND]: appBrand ?? HQO_BRAND,
        ...(buildingUuid && { [HqoHeadersEnum.HQO_BUILDING_UUID]: buildingUuid }),
      }),
    }),
    [authToken, apiUrl, appBrand, setAuthToken],
  );

  return <ApiContext.Provider value={contextValue}>{children}</ApiContext.Provider>;
};

export const useApi = <K extends keyof ApiContextValue | undefined = undefined>(key?: K) =>
  useContextValue(ApiContext, key);
